import * as React from 'react';
import { useAuth } from './AuthProvider';
import { useAgency } from './AgencyProvider';
import {
  isPermissionKey,
  isRole,
  isRoleRule,
  ROLE_RULES,
  type Gate,
  type PermissionRow,
  type PermissionSource,
} from '@/data/permissions';

/**
 * What the caller may do in the current agency (design 3). Reads `agency_permissions(p_agency)` once
 * per agency and keeps every row (the settings page shows all four roles).
 *
 * `can(gate)`, in order: no ready agency gives false; a ROLE_RULES gate checks the role only; `admin`
 * gives true for every matrix key (the server short-circuits the same way, so it is true even while
 * loading); otherwise the (role, key) row of a ready matrix; false while loading or on error, so a
 * button appears a moment late rather than flashing and vanishing.
 *
 * This is courtesy, not security: every RPC re-checks, and a 42501 still maps to readable copy.
 */

export interface Permissions {
  status: 'loading' | 'error' | 'ready';
  rows: PermissionRow[];
  can(gate: Gate): boolean;
  reload(): void;
}

interface RawRow {
  role: string;
  permission: string;
  group_key: string;
  sort_order: number | string;
  allowed: boolean;
  source: string;
}

const PermissionsContext = React.createContext<Permissions | null>(null);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { client } = useAuth();
  const agency = useAgency();
  const agencyId = agency.status === 'ready' ? agency.agency.id : null;
  const role = agency.status === 'ready' ? agency.role : null;

  const [state, setState] = React.useState<{ status: Permissions['status']; rows: PermissionRow[] }>({
    status: 'loading',
    rows: [],
  });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  const warned = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (!agencyId) {
      setState({ status: 'loading', rows: [] });
      return;
    }
    let active = true;
    setState((s) => ({ status: s.rows.length ? s.status : 'loading', rows: s.rows }));
    void (async () => {
      const { data, error } = await client.rpc('agency_permissions', { p_agency: agencyId });
      if (!active) return;
      if (error) {
        console.warn('PermissionsProvider: agency_permissions failed', error);
        setState({ status: 'error', rows: [] });
        return;
      }
      const rows: PermissionRow[] = ((data ?? []) as RawRow[])
        .filter((r) => isRole(r.role))
        .map((r) => ({
          role: r.role as PermissionRow['role'],
          permission: r.permission,
          groupKey: r.group_key,
          sortOrder: Number(r.sort_order),
          allowed: r.allowed === true,
          source: (['admin', 'agency', 'default'].includes(r.source) ? r.source : 'default') as PermissionSource,
        }));
      setState({ status: 'ready', rows });
    })();
    return () => {
      active = false;
    };
  }, [client, agencyId, nonce]);

  const can = React.useCallback(
    (gate: Gate): boolean => {
      if (!role) return false;
      if (isRoleRule(gate)) return (ROLE_RULES[gate] as readonly string[]).includes(role);
      if (!isPermissionKey(gate)) return false;
      if (role === 'admin') return true;
      if (state.status !== 'ready') return false;
      const row = state.rows.find((r) => r.role === role && r.permission === gate);
      if (!row) {
        if (!warned.current.has(gate)) {
          warned.current.add(gate);
          console.warn(`PermissionsProvider: ${gate} is not in agency_permissions (catalog drift?)`);
        }
        return false;
      }
      return row.allowed;
    },
    [role, state],
  );

  const value = React.useMemo<Permissions>(
    () => ({ status: state.status, rows: state.rows, can, reload }),
    [state, can, reload],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions(): Permissions {
  const ctx = React.useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used inside PermissionsProvider');
  return ctx;
}
